import { LoadingLoop, Modal } from '@medeloop/medeloop-components-library';
import { useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { removeUserFromDataSource } from 'src/api/users';
import { selectAuthToken } from 'src/store/selectors';
import { useDataSourceDetailsContext } from './details';
import { useTranslation } from 'react-i18next';

enum DataSourceAction {
  UNASSIGN_RESEARCHER = 'UNASSIGN_RESEARCHER',
}

interface DataSourceActionParameters {
  dataSourceId: string;
  action: string;
  name: string;
}

interface DataSourceActionResults {
  title: string;
  question: string;
  confirm: string;
  apiCall: Function;
}

const selectParameters = ({
  dataSourceId,
  action,
  name,
}: DataSourceActionParameters): DataSourceActionResults => {
  const { t } = useTranslation();

  switch (action) {
    case 'UNASSIGN_RESEARCHER': {
      return {
        title: t('unassign_researcher'),
        question: t('unassign_researcher_from_data_source_confirmation', {
          name,
          id: dataSourceId,
        }),
        confirm: t(`unassign_researcher`),
        apiCall: removeUserFromDataSource,
      };
    }

    default: {
      return {
        title: '',
        question: '',
        confirm: '',
        apiCall: () => {},
      };
    }
  }
};

export const DataSourceActionsPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useSelector(selectAuthToken);

  const name = params.get('name') || '';
  const dataSourceId = params.get('dataSourceId') || '';
  const action = params.get('action') || '';
  const role = params.get('role') || '';
  const userId = params.get('userId') || '';

  const { container } = useDataSourceDetailsContext();

  // If none of the attributes are present, go back.
  if (!name || !dataSourceId || !action || !(action in DataSourceAction)) {
    navigate(-1);
  }

  const { title, question, confirm, apiCall } = selectParameters({
    dataSourceId,
    action,
    name,
  });

  const { isPending, mutate } = useMutation({
    mutationFn: () => {
      return apiCall({ token, dataSourceId, role, userId });
    },
    onSuccess: () => {
      if (action === 'DELETE') navigate('/overview');
      else navigate(`/overview/data-sources/${dataSourceId}`);
    },
  });

  const handleConfirm = () => {
    mutate();
  };

  return (
    <Modal
      isOpen
      title={title}
      actionText={confirm}
      actionFunction={handleConfirm}
      actionType={action === 'DELETE' ? 'danger' : 'primary'}
      onCancel={() => navigate(-1)}
      container={container}
    >
      <div className="w-96">
        {isPending ? (
          <div className="flex flex-grow items-center justify-center">
            <LoadingLoop className="w-16" />
          </div>
        ) : (
          question
        )}
      </div>
    </Modal>
  );
};
